import React, { useEffect, useContext } from 'react';

/**
 * PendingPaymentNotifier component - Handles displaying toast notifications for pending payments.
 * 
 * @description
 * This component is responsible for:
 * - Fetching pending payment details for the current user
 * - Displaying persistent toast notifications for each pending payment
 * - Listening for payment update events to refresh notifications
 * - Cleaning up event listeners and notifications when unmounted
 * - Only executes if enterpriseData exists and is valid
 *
 * @example
 * <PendingPaymentNotifier />
 *
 * @returns {null} This component doesn't render any visible UI, it only manages notifications
 */
import { toast as sonnerToast } from 'sonner';
import { useMediaQuery } from 'react-responsive';
import { AuthContext } from '../../../core/context/AuthContext';
import { useInitialFetch } from '../../../core/context/InitialFetchContext';
import API_ENDPOINTS from '../../../core/constant/API_ENDPOINTS';
import { getData } from '../../../core/services/api';
import { formatDateToMonthDayYear } from '../../../core/utils/dateUtils';
import PendingPaymentToast from './PendingPaymentToast';
import { emitter } from '../../../core/event/envents'; // Asegúrate de que la ruta es correcta

const PendingPaymentNotifier: React.FC = () => {
  /**
   * Fetches payment details and displays notifications for pending payments
   * @async
   * @function fetchAndShowNotifications
   * @returns {Promise<void>}
   */
  const context = useContext(AuthContext);
  const user = context?.user;
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const { enterpriseData } = useInitialFetch();

  // Función para cargar datos y mostrar notificaciones
  const fetchAndShowNotifications = async () => {
    if (!user || !user.userId) return;
    if ( enterpriseData?.isActivePurchaseAutomatic !== false ) return;

    sonnerToast.dismiss(); // Limpia notificaciones anteriores

    try {
      const response = await getData(`${API_ENDPOINTS.paymentDetails}?customerId=${user.userId}`, { size: 20 });
      const payments = response.data.data.content;
      const pendingPayments = payments.filter((payment: any) => !payment.isActivated);

      pendingPayments.forEach((payment: any) => {
        const toastId = sonnerToast(
          () => (
            <PendingPaymentToast
              payment={payment}
              isDesktop={isDesktop}
              toastId={toastId}
              formatDateToLongSpanish={formatDateToMonthDayYear}
            />
          ),
          { duration: Infinity } // Notificación persistente
        );
      });
    } catch (error) {
      console.error("Error fetching payment details:", error);
      sonnerToast.error("Error al obtener los detalles de pago.", { duration: Infinity });
    }
  };

  /**
   * Sets up event listeners and initial data fetch
   * @effect
   * @listens paymentCompleted - Refreshes notifications when payment is completed
   * @listens paymentActivated - Refreshes notifications when payment is activated
   * @dependencies user, isDesktop - Re-runs effect when user or screen size changes
   */
  useEffect(() => {
    emitter.on('paymentCompleted', fetchAndShowNotifications);
    emitter.on('paymentActivated', fetchAndShowNotifications);
    fetchAndShowNotifications();

    return () => {
      emitter.off('paymentCompleted', fetchAndShowNotifications);
      emitter.off('paymentActivated', fetchAndShowNotifications);
      sonnerToast.dismiss();
    };
}, [user, isDesktop, enterpriseData]); // Dependencias: user e isDesktop

  return null;
};

export default PendingPaymentNotifier;